import { of, throwError } from 'rxjs';
import { AjaxError, AjaxRequest, AjaxResponse } from 'rxjs/ajax';
import { filter, take } from 'rxjs/operators';
import { Http } from '../data/http';
import { Net } from '../data/net';
import { RpcObservableErrorMonitorClient } from '../rpc/error-monitor/rpc-observable-error-monitor-client';
import { ErrorMonitorRecord } from '../rpc/error-monitor/rpc-observable-error-monitor-model';
import { RpcObservableErrorMonitorServer } from '../rpc/error-monitor/rpc-observable-error-monitor-server';
import { Rpc } from '../rpc/rpc';

/**
 * Error Monitor class.
 */
export class ErrorMonitor {
    private static monitorName = 'ErrorMonitor';
    private static instance: ErrorMonitor;

    private rpc: Rpc;
    private rpcObservableErrorMonitorClient: RpcObservableErrorMonitorClient;
    private rpcObservableErrorMonitorServer: RpcObservableErrorMonitorServer;
    private enabled = false;
    private shellVersionChecked = false;
    private check: (record: ErrorMonitorRecord) => void = () => {};

    /**
     * Gets the current ErrorMonitor instance.
     */
    public static get current(): ErrorMonitor {
        if (ErrorMonitor.instance) {
            return ErrorMonitor.instance;
        }

        ErrorMonitor.instance = new ErrorMonitor();
        return ErrorMonitor.instance;
    }

    /**
     * Start and register the check function on shell.
     *
     * @param check the check function.
     */
    public startOnShell(check: (record: ErrorMonitorRecord) => void): void {
        this.enabled = true;
        this.check = check;
        this.updateMonitor();
    }

    /**
     * Start error monitoring if it was stopped once.
     */
    public startOnModule(): void {
        this.enabled = true;
        this.updateMonitor();
    }

    /**
     * Stop error monitoring if it's started. This works on both shell and module.
     */
    public stop(): void {
        this.enabled = false;
        this.check = () => {};
        this.updateMonitor();
    }

    /**
     * Register rpc and start error monitoring.
     *
     * @param rpc the rpc.
     */
    public registerRpc(rpc: Rpc): void {
        if (!this.rpc) {
            this.rpc = rpc;
            if (MsftSme.isShell()) {
                rpc.stateChanged
                    .pipe(
                        filter(active => active),
                        take(1))
                    .subscribe(() => {
                        this.rpcObservableErrorMonitorServer = new RpcObservableErrorMonitorServer(rpc);
                        this.rpcObservableErrorMonitorServer.register(
                            request => {
                                this.check(request);
                                return of(null);
                            });
                    });
            } else {
                rpc.stateChanged
                    .pipe(
                        filter(active => active),
                        take(1))
                    .subscribe(() => this.rpcObservableErrorMonitorClient = new RpcObservableErrorMonitorClient(rpc));
            }

            this.enabled = true;
            this.updateMonitor();
        }
    }

    /**
     * Report the error from the ajax error object.
     *
     * @param error the ajax error object.
     */
    public reportErrorFromAjax(error: AjaxError): void {
        if (!error || !error.request || !error.request.url) {
            return;
        }

        const forbidden = error.xhr && error.xhr.response && error.xhr.response.error && error.xhr.response.error.forbidden;
        const statusCode = error.status;
        const request = error.request;
        const url = request.url;
        const method = request.method;
        const message = Net.getErrorMessage(error);
        let code = null;
        let action = null;
        if (error && error.xhr && error.xhr.response && error.xhr.response.error) {
            code = error.xhr.response.error.code || error.xhr.response.error.errorCode;
            action = error.xhr.response.error.actionCode;
        }

        this.reportError({ httpError: { url, method, statusCode, message, code, action, forbidden } });
    }

    /**
     * Report the error to shell.
     *
     * @param error the error monitor record.
     */
    public reportError(error: ErrorMonitorRecord): void {
        if (!this.enabled) {
            return;
        }

        const self = MsftSme.self();
        const record: ErrorMonitorRecord = {
            ...error,
            ...{ sessionId: self.Init.sessionId, timestamp: Date.now(), moduleName: self.Init.moduleName }
        };

        if (this.rpc && this.rpc.stateActive && this.rpcObservableErrorMonitorClient) {
            // send to shell.
            this.rpcObservableErrorMonitorClient.report(record).subscribe();
        } else {
            this.check(record);
        }
    }

    /**
     * Update the monitoring state.
     */
    private updateMonitor(): void {
        if (this.enabled) {
            this.registerHttp();
        } else {
            Http.unregisterMonitors(ErrorMonitor.monitorName);
        }
    }

    /**
     * Register Http monitoring.
     */
    private registerHttp(): void {
        Http.registerMonitorSet({
            name: ErrorMonitor.monitorName,
            preMonitor: (request: AjaxRequest) => of(request),
            successMonitor: (response: AjaxResponse<any>) => of(response),
            errorMonitor: (error: AjaxError) => {
                this.reportErrorFromAjax(error);
                return throwError(() => error);
            }
        });
    }
}
